/**
 * Client-side state machine that owns an adaptive practice session. Consumed
 * by the adaptive-only Practice page.
 *
 * Split across two React contexts: `SessionMetaContext` carries
 * rarely-changing metadata (node, timecap, mastery) and is subscribed by the
 * header strip; `SessionActivityContext` carries the frequently-changing
 * current-attempt state and is subscribed by the question body. The split
 * prevents a key-press on the MCQ radio from triggering a timecap countdown
 * re-render.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { SESSION_CLOSED_CODE } from '@shared/revisionSession';
import { sandboxStorageKey } from '@/auth/sandboxSelection';
import type {
  AdaptiveQuestionPayload,
  ConfidenceSignal,
  SessionMode,
} from '@/components/Practice/adaptive/types';
import { useStaffLevelStreamContext } from '@/contexts/staffLevelStreamContext';
import {
  type AdaptiveAttemptResponse,
  adaptivePracticeApi,
  type AdaptiveSessionMeta,
  type AdaptiveSessionReviewAttempt,
  type AdaptiveSessionSummary,
  type AdaptiveStreakInfo,
  type DiagnosticPlacement,
  type PreferredNodeReason,
  type SessionEndReason,
} from '@/services/adaptivePracticeApi';
import { apiErrorCode } from '@/services/apiUtils';
import { practiceSessionsApi } from '@/services/practiceSessionsApi';
import { useDevLoadingTrace } from '@/utils/devLoadingTrace';

import { useAuthenticatedCall } from './useAuthenticatedCall';

/**
 * Key used to persist the currently-active adaptive sessionId across full
 * page reloads. On mount, SessionRunningView reads this to attempt a
 * rehydrate via `/state` before bouncing to home.
 */
export const ADAPTIVE_SESSION_STORAGE_KEY = sandboxStorageKey(
  'deepthink.adaptive.sessionId'
);

const readStoredSessionId = (): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(ADAPTIVE_SESSION_STORAGE_KEY);
  } catch {
    return null;
  }
};

const writeStoredSessionId = (sessionId: string | null): void => {
  if (typeof window === 'undefined') return;
  try {
    if (sessionId) {
      window.localStorage.setItem(ADAPTIVE_SESSION_STORAGE_KEY, sessionId);
    } else {
      window.localStorage.removeItem(ADAPTIVE_SESSION_STORAGE_KEY);
    }
  } catch {
    // Private-mode Safari throws on localStorage writes; refresh-safety is a
    // progressive enhancement so we swallow and let the UX fall back to the
    // pre-existing "bounce home" behavior.
  }
};

// ── Public types ───────────────────────────────────────────────────────

export interface CurrentQuestionState {
  question: AdaptiveQuestionPayload;
  selectedKey: string | null;
  confidenceSignal: ConfidenceSignal | null;
  /** Wall-clock ms at the moment the question finished rendering locally. */
  renderedAt: number;
  /** Populated after submit; drives the MisconceptionFeedback panel. */
  result: AdaptiveAttemptResponse | null;
  submitting: boolean;
}

/**
 * Running totals updated on every successful submit. Separate from `summary`
 * because `summary` is only populated by `endSession` and is therefore `null`
 * while the session is in flight — the mid-session daily-completion
 * celebration gate needs a value it can read *before* the session ends.
 */
export interface LiveSessionProgress {
  totalAttempts: number;
  correctCount: number;
  /**
   * Where the practice day stands after the last saved answer, from the
   * attempt response (docs/designs/daily-goal-2026-10-12). Null until this
   * session saves an answer; the view falls back to the daily preview.
   */
  day: {
    startsAt?: string;
    answeredToday: number;
    correctToday: number;
    levelUpsToday: number;
    reviewsClearedToday: number;
  } | null;
  /** What the last saved answer did to its skill. Null until one is saved. */
  lastAnswer: { levelUp: boolean; reviewCleared: boolean } | null;
}

/**
 * Durable-save state machine for attempt submits. Lives alongside
 * `current.submitting` but covers the full retry window so the UI can gate
 * the correctness flip on a confirmed server write instead of a fire-and-forget
 * send. Transitions: idle → submitting → (success: idle | 5xx: retrying → idle
 * | retrying-then-5xx: failed). From `failed`, a manual `submitCurrent` call
 * resets back to `submitting`.
 */
export type SubmitState = 'idle' | 'submitting' | 'retrying' | 'failed';

export interface UsePracticeSessionApi {
  meta: AdaptiveSessionMeta | null;
  summary: AdaptiveSessionSummary | null;
  /** Per-attempt review rows populated on `/end`. Empty until close. */
  reviewAttempts: AdaptiveSessionReviewAttempt[];
  /** True when the server capped the review list at SESSION_REVIEW_ATTEMPT_CAP. */
  reviewTruncated: boolean;
  /** Streak snapshot returned by `/end`. Null until close. */
  streak: AdaptiveStreakInfo | null;
  /**
   * Frozen placement profile returned by `/end` for sessions minted as the
   * cold-start diagnostic. Null until close and for ordinary sessions.
   * Drives the mandatory placement summary on /diagnostic.
   */
  placement: DiagnosticPlacement | null;
  liveProgress: LiveSessionProgress;
  /**
   * How many answers in a row the server's rapid-guess guard flagged as
   * sub-floor. Resets to 0 on any unflagged answer and on session reset. The
   * session view nudges when this crosses
   * `RAPID_GUESS_GUARD.nudgeAfterConsecutiveFlags`.
   */
  consecutiveRapidGuesses: number;
  current: CurrentQuestionState | null;
  resumed: boolean;
  /**
   * True when this tab's session was closed because a new one was started
   * (question request answered 409 `session_closed`). The recap of the
   * closed session is loaded and shown under the notice.
   */
  sessionClosed: boolean;
  status: 'idle' | 'starting' | 'running' | 'ended' | 'error';
  errorMessage: string | null;
  /**
   * The value thrown behind `errorMessage`, kept unflattened. `fetchWithConfig`
   * spreads the response body onto the Error, so this is the only place the
   * adaptive-egress code, HTTP status and `resetAt` survive — flatten it to
   * `err.message` and `friendlyPracticeError` can no longer tell a spent daily
   * allowance from a burst-limit 429. Render errors from here, not from
   * `errorMessage`.
   */
  error: unknown;
  /** Durable-save state for the currently-submitting attempt. */
  submitState: SubmitState;

  startSession: (opts: {
    sessionMode: SessionMode;
    subject?: string;
    topic?: string;
    subtopic?: string;
    preferredStartNodeId?: string;
    /** Revision topic id: keeps the session inside that earlier-year chapter. */
    preferredStartTopicId?: string;
    /** Exam-layout question type id inside that chapter. */
    preferredStartQuestionTypeId?: string;
    timeCapMs?: number;
  }) => Promise<{
    repointed: boolean;
    preferredNodeAvailable?: boolean;
    preferredNodeReason?: PreferredNodeReason;
  }>;

  /**
   * Mint or resume a Continue Practice (free-session) drill that runs the
   * adaptive scheduler across the student's full set of in-scope, non-mastered
   * nodes. When the server reports the student has nothing left to practise
   * (`empty: true`), status stays `idle` and the caller renders an empty-state
   * card instead of navigating into the session view.
   */
  startFreeSession: (opts?: {
    subject?: string;
    topic?: string;
    subtopic?: string;
  }) => Promise<
    | { empty: false; resumed: boolean }
    | { empty: true; reason: 'all_mastered' | 'no_in_scope_nodes' }
  >;

  fetchNextQuestion: (params: {
    userId: string;
    subject: string;
    topic?: string;
    subtopic?: string;
  }) => Promise<void>;

  selectKey: (key: string) => void;
  setConfidence: (signal: ConfidenceSignal) => void;

  submitCurrent: (params: {
    userId: string;
    subject: string;
    topic?: string;
    subtopic?: string;
  }) => Promise<void>;

  /**
   * Close the active session. Resolves with the server-returned streak
   * snapshot so callers can gate the daily-goal celebration on
   * `bumpedThisSession` without reading stale hook state. Resolves `null`
   * when no session is active or the request errored.
   */
  endSession: (reason: SessionEndReason) => Promise<AdaptiveStreakInfo | null>;
  /**
   * Restore state for an in-flight session the user refreshed away from.
   * Called by SessionRunningView on mount when `status === 'idle'` and a
   * persisted sessionId is in localStorage. On 404/410 the stored id is
   * cleared and status stays idle so the caller can navigate home.
   */
  rehydrateSession: (sessionId: string) => Promise<boolean>;
  reset: () => void;
}

// ── Hook ───────────────────────────────────────────────────────────────

const INITIAL_LIVE_PROGRESS: LiveSessionProgress = {
  totalAttempts: 0,
  correctCount: 0,
  day: null,
  lastAnswer: null,
};

export function usePracticeSession(): UsePracticeSessionApi {
  const { callWithAuth } = useAuthenticatedCall();
  // Active staff "trying out as" cohort, when one is selected. Forwarded onto
  // every adaptive endpoint as `?orgLevel=…&examLevel=…` so the server scopes
  // questions / mastery / target picks to the simulated cohort. Null for
  // students and for staff who haven't picked a cohort.
  const { selected: staffLevelStream } = useStaffLevelStreamContext();
  const [meta, setMeta] = useState<AdaptiveSessionMeta | null>(null);
  const [summary, setSummary] = useState<AdaptiveSessionSummary | null>(null);
  const [reviewAttempts, setReviewAttempts] = useState<
    AdaptiveSessionReviewAttempt[]
  >([]);
  const [reviewTruncated, setReviewTruncated] = useState<boolean>(false);
  const [streak, setStreak] = useState<AdaptiveStreakInfo | null>(null);
  const [placement, setPlacement] = useState<DiagnosticPlacement | null>(null);
  const [liveProgress, setLiveProgress] = useState<LiveSessionProgress>(
    INITIAL_LIVE_PROGRESS
  );
  const [consecutiveRapidGuesses, setConsecutiveRapidGuesses] = useState(0);
  const [current, setCurrent] = useState<CurrentQuestionState | null>(null);
  const [resumed, setResumed] = useState(false);
  const [sessionClosed, setSessionClosed] = useState(false);
  const [status, setStatus] = useState<UsePracticeSessionApi['status']>('idle');
  // One state, two views: `thrown` keeps the original error (and with it the
  // egress code / status / resetAt callers need to pick copy), `message` keeps
  // each call site's fallback wording for a non-Error throw. Holding them
  // together is what stops the pair from drifting apart.
  const [failure, setFailure] = useState<{
    thrown: unknown;
    message: string;
  } | null>(null);
  const errorMessage = failure?.message ?? null;
  const error = failure?.thrown ?? null;
  const [submitState, setSubmitState] = useState<SubmitState>('idle');

  // Guard against stale async writes when the component unmounts mid-fetch.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const traceDetails = useMemo(
    () => ({
      status,
      submitState,
      hasMeta: Boolean(meta),
      sessionId: meta?.sessionId ?? null,
      sessionMode: meta?.sessionMode ?? null,
      practiceMode: meta?.practiceMode ?? null,
      currentNodeId: meta?.currentNodeId ?? null,
      currentNodeName: meta?.currentNodeName ?? null,
      hasCurrentQuestion: Boolean(current),
      currentQuestionId: current?.question._id ?? null,
      attemptId: current?.question.attemptId ?? null,
      selectedKey: current?.selectedKey ?? null,
      hasResult: Boolean(current?.result),
      currentSubmitting: Boolean(current?.submitting),
      liveAttempts: liveProgress.totalAttempts,
      liveCorrect: liveProgress.correctCount,
      staffOrgLevel: staffLevelStream?.orgLevel ?? null,
      staffExamLevel: staffLevelStream?.examLevel ?? null,
      hasError: Boolean(errorMessage),
      errorMessage,
    }),
    [
      current,
      errorMessage,
      liveProgress.correctCount,
      liveProgress.totalAttempts,
      meta,
      staffLevelStream?.examLevel,
      staffLevelStream?.orgLevel,
      status,
      submitState,
    ]
  );

  useDevLoadingTrace(
    'adaptive.session.status-starting',
    status === 'starting',
    {
      ...traceDetails,
      starting: status === 'starting',
    }
  );

  useDevLoadingTrace(
    'adaptive.session.submit-state',
    submitState !== 'idle' || Boolean(current?.submitting),
    {
      ...traceDetails,
      submitActive: submitState !== 'idle' || Boolean(current?.submitting),
    }
  );

  useDevLoadingTrace(
    'adaptive.session.waiting-current-question',
    status === 'running' && !current,
    {
      ...traceDetails,
      waitingForCurrentQuestion: status === 'running' && !current,
    }
  );

  const safeSet = useCallback(<T>(setter: (v: T) => void, value: T) => {
    if (mounted.current) setter(value);
  }, []);

  /**
   * Records a failure and returns the message it resolved to, so a call site
   * that also rethrows can reuse the same wording.
   */
  const fail = useCallback(
    (thrown: unknown, fallback: string): string => {
      const message = thrown instanceof Error ? thrown.message : fallback;
      safeSet(setFailure, { thrown, message });
      return message;
    },
    [safeSet]
  );

  const reset = useCallback(() => {
    setMeta(null);
    setSummary(null);
    setReviewAttempts([]);
    setReviewTruncated(false);
    setStreak(null);
    setPlacement(null);
    setLiveProgress(INITIAL_LIVE_PROGRESS);
    setConsecutiveRapidGuesses(0);
    setCurrent(null);
    setResumed(false);
    setSessionClosed(false);
    setStatus('idle');
    setFailure(null);
    setSubmitState('idle');
    writeStoredSessionId(null);
  }, []);

  const startSession = useCallback<UsePracticeSessionApi['startSession']>(
    async opts => {
      setStatus('starting');
      setFailure(null);
      setPlacement(null);
      // Nudge runs are per-session: a Home → Start round-trip that reuses this
      // mounted hook must not inherit the previous session's flag streak.
      setConsecutiveRapidGuesses(0);
      try {
        const {
          session,
          resumed: didResume,
          summary: resumedSummary,
          repointed,
          preferredNodeAvailable,
          preferredNodeReason,
        } = await callWithAuth(token =>
          adaptivePracticeApi.startSession({ ...opts, staffLevelStream }, token)
        );
        safeSet(setMeta, session);
        safeSet(setResumed, didResume);
        // A recap can start the next sitting without unmounting this hook.
        // Clear the previous question only once the new start succeeds, so
        // failed starts retain the recap and can be retried.
        safeSet(setCurrent, null);
        safeSet(setSummary, null);
        safeSet(setReviewAttempts, []);
        safeSet(setReviewTruncated, false);
        safeSet(setStreak, null);
        safeSet(setSessionClosed, false);
        safeSet(setSubmitState, 'idle');
        // Fresh starts begin at zero. Resumed sessions seed from the server
        // summary so the mid-session target-reached gate (answered >= target)
        // and the daily-completion celebration path survive a refresh or a
        // Home → Start round-trip into an already-active session.
        if (didResume && resumedSummary) {
          safeSet(setSummary, resumedSummary);
          safeSet(setLiveProgress, {
            ...INITIAL_LIVE_PROGRESS,
            totalAttempts: resumedSummary.totalAttempts,
            correctCount: resumedSummary.correctCount,
          });
        } else {
          safeSet(setLiveProgress, INITIAL_LIVE_PROGRESS);
        }
        safeSet(setStatus, 'running');
        // Persist so a full-page refresh on /practice/session rehydrates
        // instead of bouncing home (see `rehydrateSession`).
        writeStoredSessionId(session.sessionId);
        return {
          repointed: Boolean(repointed),
          ...(typeof preferredNodeAvailable === 'boolean'
            ? { preferredNodeAvailable }
            : {}),
          ...(preferredNodeReason ? { preferredNodeReason } : {}),
        };
      } catch (err: unknown) {
        const message = fail(err, 'Failed to start');
        safeSet(setStatus, 'error');
        // Rethrow so HomeView/TopicDetailView's outer try/catch can surface a
        // retry affordance. Without this, the views' `startError` state stays
        // null on a 500/401 and the student sees a spinner that silently
        // stops with no error strip.
        throw err instanceof Error ? err : new Error(message);
      }
    },
    [fail, safeSet, callWithAuth, staffLevelStream]
  );

  const startFreeSession = useCallback<
    UsePracticeSessionApi['startFreeSession']
  >(
    async opts => {
      setStatus('starting');
      setFailure(null);
      setPlacement(null);
      // Nudge runs are per-session: a Home → Start round-trip that reuses this
      // mounted hook must not inherit the previous session's flag streak.
      setConsecutiveRapidGuesses(0);
      try {
        const response = await callWithAuth(token =>
          adaptivePracticeApi.startFreeSession(
            { ...(opts ?? {}), staffLevelStream },
            token
          )
        );
        if ('empty' in response && response.empty) {
          // Nothing left to practise. Leave status at 'idle' so the home
          // screen renders the celebratory empty card; no navigation.
          safeSet(setStatus, 'idle');
          return { empty: true, reason: response.reason };
        }
        const {
          session,
          resumed: didResume,
          summary: resumedSummary,
        } = response;
        safeSet(setMeta, session);
        safeSet(setResumed, didResume);
        // A recap can start the next sitting without unmounting this hook.
        // Clear the previous question only once the new start succeeds, so
        // failed starts retain the recap and can be retried.
        safeSet(setCurrent, null);
        safeSet(setSummary, null);
        safeSet(setReviewAttempts, []);
        safeSet(setReviewTruncated, false);
        safeSet(setStreak, null);
        safeSet(setSessionClosed, false);
        safeSet(setSubmitState, 'idle');
        if (didResume && resumedSummary) {
          safeSet(setSummary, resumedSummary);
          safeSet(setLiveProgress, {
            ...INITIAL_LIVE_PROGRESS,
            totalAttempts: resumedSummary.totalAttempts,
            correctCount: resumedSummary.correctCount,
          });
        } else {
          safeSet(setLiveProgress, INITIAL_LIVE_PROGRESS);
        }
        safeSet(setStatus, 'running');
        writeStoredSessionId(session.sessionId);
        return { empty: false, resumed: didResume };
      } catch (err: unknown) {
        const message = fail(err, 'Failed to start');
        safeSet(setStatus, 'error');
        throw err instanceof Error ? err : new Error(message);
      }
    },
    [fail, safeSet, callWithAuth, staffLevelStream]
  );

  const rehydrateSession = useCallback<
    UsePracticeSessionApi['rehydrateSession']
  >(
    async sessionId => {
      if (!sessionId) return false;
      setStatus('starting');
      setFailure(null);
      try {
        const { session, summary: currentSummary } = await callWithAuth(token =>
          adaptivePracticeApi.getSessionState(sessionId, token)
        );
        safeSet(setMeta, session);
        safeSet(setSummary, currentSummary);
        // Seed `liveProgress` from the server summary so the mid-session
        // target-reached gate and daily-completion celebration survive a
        // refresh. The server is authoritative; the client counter mirrors it.
        safeSet(setLiveProgress, {
          ...INITIAL_LIVE_PROGRESS,
          totalAttempts: currentSummary.totalAttempts,
          correctCount: currentSummary.correctCount,
        });
        safeSet(setResumed, true);
        safeSet(setStatus, 'running');
        return true;
      } catch (err: unknown) {
        const apiErr = err as { status?: number } | null;
        // 404 (gone) / 410 (ended) / 403 (tenant mismatch) are terminal for
        // this sessionId — clear storage so we don't loop on the next refresh.
        if (
          apiErr &&
          (apiErr.status === 404 ||
            apiErr.status === 410 ||
            apiErr.status === 403)
        ) {
          writeStoredSessionId(null);
        }
        safeSet(setStatus, 'idle');
        return false;
      }
    },
    [safeSet, callWithAuth]
  );

  /**
   * The server answered 409 `session_closed`: a start elsewhere superseded
   * this tab's session, or it expired or was ended from another tab. Show
   * that session's own recap under the closed notice — never the new
   * session's questions — and forget the stored id so a refresh does not try
   * to resume it either. Shared by the question fetch and the answer submit,
   * which are the two calls a stale tab can still make.
   */
  const showClosedSessionRecap = useCallback(
    async (sessionId: string): Promise<void> => {
      safeSet(setSessionClosed, true);
      safeSet(setCurrent, null);
      writeStoredSessionId(null);
      try {
        const recap = await callWithAuth(token =>
          practiceSessionsApi.getRecap(sessionId, undefined, token)
        );
        safeSet(setMeta, recap.session);
        safeSet(setSummary, recap.summary);
        safeSet(setReviewAttempts, recap.attempts ?? []);
        safeSet(setReviewTruncated, Boolean(recap.truncated));
        safeSet(setPlacement, recap.placement ?? null);
        safeSet(setStatus, 'ended');
      } catch (recapErr: unknown) {
        fail(recapErr, 'Failed to load session recap');
        safeSet(setStatus, 'error');
      }
    },
    [safeSet, callWithAuth, fail]
  );

  const fetchNextQuestion = useCallback<
    UsePracticeSessionApi['fetchNextQuestion']
  >(
    async params => {
      setFailure(null);
      try {
        const response = await callWithAuth(token =>
          adaptivePracticeApi.fetchNextQuestion(
            params.userId,
            params.subject,
            token,
            params.topic,
            params.subtopic,
            staffLevelStream,
            meta?.sessionId ?? null
          )
        );
        if (!response || !response.question) {
          // Server signals session is exhausted (204). Auto-close so the
          // student lands on the recap. If this happens before any attempts,
          // close the empty server session and bounce home instead of showing
          // a zero-attempt recap.
          if (meta) {
            try {
              if (liveProgress.totalAttempts === 0) {
                await callWithAuth(token =>
                  adaptivePracticeApi.endSession(
                    meta.sessionId,
                    token,
                    'target_reached'
                  )
                );
                if (mounted.current) reset();
                else writeStoredSessionId(null);
                return;
              }

              // Keep `current` in place during the endSession round-trip so
              // the view doesn't fall back to "Loading next question…"
              // mid-transition.
              const {
                session: endedSession,
                summary: finalSummary,
                attempts: finalAttempts,
                truncated: finalTruncated,
                streak: finalStreak,
                placement: finalPlacement,
              } = await callWithAuth(token =>
                adaptivePracticeApi.endSession(
                  meta.sessionId,
                  token,
                  'target_reached'
                )
              );
              safeSet(setMeta, endedSession);
              safeSet(setSummary, finalSummary);
              safeSet(setReviewAttempts, finalAttempts ?? []);
              safeSet(setReviewTruncated, Boolean(finalTruncated));
              safeSet(setStreak, finalStreak ?? null);
              safeSet(setPlacement, finalPlacement ?? null);
              safeSet(setStatus, 'ended');
              writeStoredSessionId(null);
            } catch (endErr: unknown) {
              fail(endErr, 'Failed to end session');
              // Surface failure via status so SessionRunningView's error Alert
              // fires and SessionRecapView's idle/spinner branch doesn't hang
              // forever waiting on a summary that will never arrive.
              safeSet(setStatus, 'error');
            }
          } else {
            safeSet(setCurrent, null);
          }
          return;
        }
        safeSet(setCurrent, {
          question: response.question,
          selectedKey: null,
          confidenceSignal: null,
          renderedAt: Date.now(),
          result: null,
          submitting: false,
        });
        // Adaptive sessions hop nodes mid-session: the server advances
        // `currentNodeId` at serve time and echoes the served node here. Fold
        // it into `meta` so the header chip labels the question actually on
        // screen — meta is otherwise only set at start/rehydrate, which left
        // the PICKED FOR YOU chip frozen on the session-start node. Guarded on a
        // non-empty id so servers without the field leave meta untouched.
        if (response.currentNodeId && mounted.current) {
          const servedNodeId = response.currentNodeId;
          const servedNodeName = response.currentNodeName ?? null;
          setMeta(prev => {
            if (!prev) return prev;
            // During a rolling deploy, an older server may omit the new
            // reason while still echoing the same node id/name. Preserve the
            // start-response reason for that same node; never carry it across
            // a node hop where it could describe the wrong skill.
            const servedNodeReason =
              response.currentNodeReason === undefined &&
              prev.currentNodeId === servedNodeId
                ? prev.currentNodeReason
                : (response.currentNodeReason ?? null);
            // The evidence travels with the reason it explains: a server
            // that sent no evidence clears it, so a stale sentence never
            // describes a different question.
            const servedNodeReasonEvidence =
              response.currentNodeReasonEvidence ?? null;
            return prev.currentNodeId !== servedNodeId ||
              prev.currentNodeName !== servedNodeName ||
              prev.currentNodeReason !== servedNodeReason ||
              prev.currentNodeReasonEvidence !== servedNodeReasonEvidence
              ? {
                  ...prev,
                  currentNodeId: servedNodeId,
                  currentNodeName: servedNodeName,
                  currentNodeReason: servedNodeReason,
                  currentNodeReasonEvidence: servedNodeReasonEvidence,
                }
              : prev;
          });
        }
        safeSet(setResumed, Boolean(response.resumed));
      } catch (err: unknown) {
        if (apiErrorCode(err) === SESSION_CLOSED_CODE && meta) {
          // Another start closed this tab's session. Show its own recap,
          // never the new session's questions.
          await showClosedSessionRecap(meta.sessionId);
          return;
        }
        fail(err, 'Failed to load question');
        // Promote transport failures to the view-level error state so
        // SessionRunningView's error Alert fires and the student gets a
        // retry affordance instead of a stuck "Loading next question…" Box.
        safeSet(setStatus, 'error');
      }
    },
    [
      meta,
      liveProgress.totalAttempts,
      reset,
      fail,
      safeSet,
      callWithAuth,
      staffLevelStream,
      showClosedSessionRecap,
    ]
  );

  const selectKey = useCallback<UsePracticeSessionApi['selectKey']>(key => {
    setCurrent(prev => (prev ? { ...prev, selectedKey: key } : prev));
  }, []);

  const setConfidence = useCallback<UsePracticeSessionApi['setConfidence']>(
    signal => {
      setCurrent(prev => (prev ? { ...prev, confidenceSignal: signal } : prev));
    },
    []
  );

  const submitCurrent = useCallback<UsePracticeSessionApi['submitCurrent']>(
    async params => {
      if (!meta) return;
      setCurrent(prev => (prev ? { ...prev, submitting: true } : prev));
      safeSet(setSubmitState, 'submitting');
      safeSet(setFailure, null);
      try {
        // Snapshot inside the setter callback to avoid a stale `current` closure.
        const snapshot = await new Promise<CurrentQuestionState | null>(
          resolve => {
            setCurrent(prev => {
              resolve(prev);
              return prev;
            });
          }
        );
        if (!snapshot || !snapshot.selectedKey) {
          safeSet(setSubmitState, 'idle');
          return;
        }

        const timeToAnswerMs = Date.now() - snapshot.renderedAt;
        const sendAttempt = () =>
          callWithAuth(token =>
            adaptivePracticeApi.submitAttempt(
              {
                userId: params.userId,
                subject: params.subject,
                topic: params.topic,
                subtopic: params.subtopic,
                questionId: snapshot.question._id,
                attemptId: snapshot.question.attemptId,
                chosenKey: snapshot.selectedKey!,
                duration: Math.round(timeToAnswerMs / 1000),
                timeToAnswerMs,
                sessionId: meta.sessionId,
                confidenceSignal: snapshot.confidenceSignal ?? undefined,
                sessionMode: meta.sessionMode,
              },
              token
            )
          );

        // Single auto-retry on 5xx. The server's write-strict fence returns
        // 500 when the immutable-fact write failed, so the correctness UI
        // stays idle until this resolves. A second failure transitions to
        // 'failed' — the amber banner offers a manual retry without
        // clobbering the question so the student doesn't lose their place.
        let result: AdaptiveAttemptResponse;
        try {
          result = await sendAttempt();
        } catch (firstErr: unknown) {
          const firstStatus = (firstErr as { status?: number })?.status ?? 0;
          if (firstStatus >= 500 || firstStatus === 0) {
            safeSet(setSubmitState, 'retrying');
            // Short delay so the banner is visible and the server gets a
            // breather. Matches the existing 1s idempotent retry cadence in
            // apiUtils.ts.
            await new Promise(r => setTimeout(r, 1500));
            try {
              result = await sendAttempt();
            } catch (retryErr: unknown) {
              fail(retryErr, 'Failed to save your answer');
              safeSet(setSubmitState, 'failed');
              setCurrent(prev =>
                prev ? { ...prev, submitting: false } : prev
              );
              return;
            }
          } else {
            // Non-5xx error (4xx, client-side). Surface immediately — no retry.
            throw firstErr;
          }
        }

        safeSet(setCurrent, {
          ...snapshot,
          result,
          submitting: false,
        });
        safeSet(setSubmitState, 'idle');
        // Bump running totals so the mid-session daily-completion gate and
        // the header progress bar have accurate values before `endSession` runs.
        if (mounted.current) {
          setLiveProgress(prev => ({
            totalAttempts: prev.totalAttempts + 1,
            correctCount: prev.correctCount + (result.correct ? 1 : 0),
            // The server's day totals after this answer, when it sent them;
            // an older server leaves the previous value in place.
            day:
              typeof result.answeredToday === 'number'
                ? {
                    startsAt: result.practiceDayStartsAt,
                    answeredToday: result.answeredToday,
                    correctToday: result.correctToday ?? 0,
                    levelUpsToday: result.levelUpsToday ?? 0,
                    reviewsClearedToday: result.reviewsClearedToday ?? 0,
                  }
                : prev.day,
            lastAnswer: {
              levelUp: result.levelUp === true,
              reviewCleared: result.reviewCleared === true,
            },
          }));
          // Consecutive-flag run for the "take your time" nudge. Counted off
          // the server's verdict, never a local `Date.now()` comparison — the
          // client has no view of the fallbacks the server applied.
          setConsecutiveRapidGuesses(prev =>
            result.rapidGuessFlagged ? prev + 1 : 0
          );
        }
      } catch (err: unknown) {
        if (apiErrorCode(err) === SESSION_CLOSED_CODE) {
          // The session closed under this tab (a Start elsewhere superseded
          // it), so the server refused the answer. Show that session's own
          // recap: it already holds everything that was saved.
          safeSet(setSubmitState, 'idle');
          await showClosedSessionRecap(meta.sessionId);
          return;
        }
        fail(err, 'Failed to submit attempt');
        setCurrent(prev => (prev ? { ...prev, submitting: false } : prev));
        safeSet(setSubmitState, 'idle');
        // 4xx / client-side errors (auth, rate limit, schema) drop into the
        // session-level error view. Write-strict 5xx failures do NOT reach
        // here — they exit early via the 'failed' submit state above.
        safeSet(setStatus, 'error');
      }
    },
    [meta, fail, safeSet, callWithAuth, showClosedSessionRecap]
  );

  const endSession = useCallback<UsePracticeSessionApi['endSession']>(
    async reason => {
      if (!meta) return null;
      try {
        const {
          session,
          summary: finalSummary,
          attempts: finalAttempts,
          truncated: finalTruncated,
          streak: finalStreak,
          placement: finalPlacement,
        } = await callWithAuth(token =>
          adaptivePracticeApi.endSession(meta.sessionId, token, reason)
        );
        safeSet(setMeta, session);
        safeSet(setSummary, finalSummary);
        safeSet(setReviewAttempts, finalAttempts ?? []);
        safeSet(setReviewTruncated, Boolean(finalTruncated));
        safeSet(setStreak, finalStreak ?? null);
        safeSet(setPlacement, finalPlacement ?? null);
        safeSet(setStatus, 'ended');
        writeStoredSessionId(null);
        return finalStreak ?? null;
      } catch (err: unknown) {
        fail(err, 'Failed to end session');
        // Surface failure via status so SessionRunningView's error Alert fires
        // instead of the UI silently staying on 'running' with a dead session.
        safeSet(setStatus, 'error');
        return null;
      }
    },
    [meta, fail, safeSet, callWithAuth]
  );

  return useMemo(
    () => ({
      meta,
      summary,
      reviewAttempts,
      reviewTruncated,
      streak,
      placement,
      liveProgress,
      consecutiveRapidGuesses,
      current,
      resumed,
      sessionClosed,
      status,
      errorMessage,
      error,
      submitState,
      startSession,
      startFreeSession,
      fetchNextQuestion,
      selectKey,
      setConfidence,
      submitCurrent,
      endSession,
      rehydrateSession,
      reset,
    }),
    [
      meta,
      summary,
      reviewAttempts,
      reviewTruncated,
      streak,
      placement,
      liveProgress,
      consecutiveRapidGuesses,
      current,
      resumed,
      sessionClosed,
      status,
      errorMessage,
      error,
      submitState,
      startSession,
      startFreeSession,
      fetchNextQuestion,
      selectKey,
      setConfidence,
      submitCurrent,
      endSession,
      rehydrateSession,
      reset,
    ]
  );
}

/** Non-hook helper exported so SessionRunningView can peek at storage on mount. */
export { readStoredSessionId };
